import React, { useState, useEffect } from 'react';
import { Heart, ThumbsUp, Laugh, Angry, Frown, Star, Plus } from 'lucide-react';
import { Button } from './button';
import { collection, addDoc, query, where, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/providers/AuthProvider';
import { cn } from '@/lib/utils';

interface MessageReactionsProps {
  messageId: string;
  className?: string;
}

interface Reaction {
  id: string;
  messageId: string;
  userId: string;
  userName: string;
  type: string;
  createdAt: Date;
}

// Use the same reactions as posts for consistency
const REACTIONS = [
  { type: 'like', icon: ThumbsUp, color: 'text-blue-500', bgColor: 'bg-blue-50', label: 'Like' },
  { type: 'love', icon: Heart, color: 'text-red-500', bgColor: 'bg-red-50', label: 'Love' },
  { type: 'haha', icon: Laugh, color: 'text-yellow-500', bgColor: 'bg-yellow-50', label: 'Haha' },
  { type: 'wow', icon: Star, color: 'text-green-500', bgColor: 'bg-green-50', label: 'Wow' },
  { type: 'sad', icon: Frown, color: 'text-purple-500', bgColor: 'bg-purple-50', label: 'Sad' },
  { type: 'angry', icon: Angry, color: 'text-orange-500', bgColor: 'bg-orange-50', label: 'Angry' },
];

export function MessageReactions({ messageId, className = '' }: MessageReactionsProps) {
  const [reactions, setReactions] = useState<Reaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!messageId) return;

    const reactionsRef = collection(db, 'messageReactions');
    const q = query(reactionsRef, where('messageId', '==', messageId));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const reactionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      })) as Reaction[];

      setReactions(reactionsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [messageId]);

  const addReaction = async (type: string) => {
    if (!user || !messageId) return;

    try {
      // Check if user already reacted with this type
      const existingReaction = reactions.find(r => r.userId === user.uid && r.type === type);
      
      if (existingReaction) {
        // Remove reaction if it already exists (toggle)
        await deleteDoc(doc(db, 'messageReactions', existingReaction.id));
      } else {
        // Remove any other reaction from this user first (only one reaction per message)
        const userOtherReaction = reactions.find(r => r.userId === user.uid);
        if (userOtherReaction) {
          await deleteDoc(doc(db, 'messageReactions', userOtherReaction.id));
        }
        
        // Add new reaction
        await addDoc(collection(db, 'messageReactions'), {
          messageId,
          userId: user.uid,
          userName: user.displayName || 'Anonymous',
          type,
          createdAt: new Date()
        });
      }
      
      setShowReactionPicker(false);
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  // Group reactions by type
  const groupedReactions = reactions.reduce((acc, reaction) => {
    if (!acc[reaction.type]) {
      acc[reaction.type] = [];
    }
    acc[reaction.type].push(reaction);
    return acc;
  }, {} as Record<string, Reaction[]>);

  const userReaction = reactions.find(r => r.userId === user?.uid);

  if (loading) return null;

  // Don't show anything if there are no reactions
  if (reactions.length === 0) return null;

  return (
    <div className={cn('flex items-center gap-1 flex-wrap', className)}>
      {/* Display existing reactions */}
      {Object.entries(groupedReactions).map(([type, typeReactions]) => {
        const reaction = REACTIONS.find(r => r.type === type);
        if (!reaction) return null;
        
        const hasUserReacted = typeReactions.some(r => r.userId === user?.uid);
        const Icon = reaction.icon;
        
        return (
          <Button
            key={type}
            variant="ghost"
            size="sm"
            className={cn(
              'h-7 px-2 text-xs rounded-full transition-all duration-200 hover:scale-105',
              hasUserReacted 
                ? cn(reaction.bgColor, reaction.color, 'border border-current') 
                : 'bg-gray-50 hover:bg-gray-100 text-gray-600'
            )}
            onClick={() => addReaction(type)}
            title={`${typeReactions.map(r => r.userName).join(', ')} reacted with ${reaction.label}`}
          >
            <Icon 
              className={cn('w-3.5 h-3.5 mr-1', reaction.color)} 
              fill={hasUserReacted && !['haha', 'sad', 'angry'].includes(type) ? 'currentColor' : 'none'}
            />
            <span className="font-medium">{typeReactions.length}</span>
          </Button>
        );
      })}
    </div>
  );
}